import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Modal, PageState, Select, TextField, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import {
  installSnippet,
  useCreateWebsiteChannel,
  useRemoveWebsiteChannel,
  useRotateSiteKey,
  useUpdateWebsiteChannel,
  useWebsiteChannels,
  type WebsiteChannel,
  type WidgetSettings,
} from '../../api/webchat';

export const WEBSITE_CHAT_MENU = 'website_chat';

const DEFAULTS: WidgetSettings = {
  title: 'Chat with us',
  greeting: 'Hi! How can we help you today?',
  color: '#25D366',
  position: 'right',
  preChat: { enabled: false, askName: true, askPhone: true, askEmail: false },
};

interface FormState {
  name: string;
  sites: string;
  settings: WidgetSettings;
}

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;

/** One website address per line (or comma-separated), trimmed, blanks dropped. */
const parseSites = (text: string) =>
  text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

/** A real domain, or a local address for testing (localhost, an IP, any port). */
export function siteLooksValid(site: string): boolean {
  try {
    const u = new URL(site.includes('://') ? site : `https://${site}`);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return false;
    return (
      u.hostname === 'localhost' || u.hostname.includes('.') || u.hostname.startsWith('[') // IPv6, e.g. [::1]
    );
  } catch {
    return false;
  }
}

function validate(f: FormState): Record<string, string> {
  const e: Record<string, string> = {};
  if (!f.name.trim()) e.name = 'Enter a name, e.g. your website.';
  else if (f.name.trim().length > 100) e.name = 'Name must be 100 characters or fewer.';
  const bad = parseSites(f.sites).filter((s) => !siteLooksValid(s));
  if (bad.length) e.allowedOrigins = `Not a website address: ${bad.join(', ')}`;
  else if (parseSites(f.sites).length > 20) e.allowedOrigins = 'At most 20 websites.';
  if (!f.settings.title.trim()) e.title = 'Enter a title.';
  else if (f.settings.title.trim().length > 60) e.title = 'Title must be 60 characters or fewer.';
  if (!f.settings.greeting.trim()) e.greeting = 'Enter a greeting.';
  else if (f.settings.greeting.trim().length > 300) {
    e.greeting = 'Greeting must be 300 characters or fewer.';
  }
  if (!/^#[0-9a-fA-F]{6}$/.test(f.settings.color)) e.color = 'Use a colour like #25D366.';
  return e;
}

/** Server issue paths (`/settings/title`, `/allowedOrigins/0`) to form fields. */
function mapServerErrors(err: ApiClientError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [path, message] of Object.entries(fieldErrorsFromApiError(err))) {
    const key = path.startsWith('allowedOrigins') ? 'allowedOrigins' : path.split('.').pop()!;
    out[key] = message;
  }
  return out;
}

function WebsiteChatModal({
  channel,
  onClose,
}: {
  /** null = a new website chat. */
  channel: WebsiteChannel | null;
  onClose: () => void;
}) {
  const create = useCreateWebsiteChannel();
  const update = useUpdateWebsiteChannel(channel?.channelId ?? '');
  const toast = useToast();
  const [form, setForm] = useState<FormState>({
    name: channel?.name ?? '',
    sites: channel?.allowedOrigins.join('\n') ?? '',
    settings: channel?.settings ?? DEFAULTS,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const busy = create.isPending || update.isPending;

  const setSetting = <K extends keyof WidgetSettings>(key: K, value: WidgetSettings[K]) => {
    setForm((f) => ({ ...f, settings: { ...f.settings, [key]: value } }));
    setErrors(({ [key]: _gone, ...rest }) => rest);
  };
  const setPreChat = (key: keyof WidgetSettings['preChat'], value: boolean) =>
    setForm((f) => ({
      ...f,
      settings: { ...f.settings, preChat: { ...f.settings.preChat, [key]: value } },
    }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }
    const settings = {
      ...form.settings,
      title: form.settings.title.trim(),
      greeting: form.settings.greeting.trim(),
    };
    try {
      if (channel) {
        await update.mutateAsync({
          name: form.name.trim(),
          allowedOrigins: parseSites(form.sites),
          settings,
        });
        toast.success('Website chat saved.');
      } else {
        const created = await create.mutateAsync({
          name: form.name.trim(),
          allowedOrigins: parseSites(form.sites),
          settings,
        });
        toast.success(
          created.starterAutomationId
            ? 'Website chat created, with a lead-capture automation.'
            : 'Website chat created. Add an automation to reply to visitors.',
        );
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const mapped = mapServerErrors(err);
        if (Object.keys(mapped).length > 0) {
          setErrors(mapped);
          setError('Please fix the highlighted fields.');
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Could not save the website chat.');
    }
  }

  return (
    <Modal title={channel ? 'Edit website chat' : 'Add website chat'} onClose={onClose}>
      <form className="ne-auth__form" onSubmit={(e) => void onSubmit(e)} noValidate>
        <TextField
          label="Name"
          placeholder="e.g. Main website"
          value={form.name}
          onChange={(e) => {
            setForm({ ...form, name: e.target.value });
            setErrors(({ name: _gone, ...rest }) => rest);
          }}
          error={errors.name}
        />
        <div className="ne-field">
          <label className="ne-field__label" htmlFor="wc-sites">
            Allowed websites (one per line)
          </label>
          <textarea
            id="wc-sites"
            className={['ne-input', errors.allowedOrigins ? 'ne-input--error' : '']
              .filter(Boolean)
              .join(' ')}
            rows={3}
            placeholder="https://www.example.com"
            value={form.sites}
            aria-invalid={errors.allowedOrigins ? true : undefined}
            onChange={(e) => {
              setForm({ ...form, sites: e.target.value });
              setErrors(({ allowedOrigins: _gone, ...rest }) => rest);
            }}
          />
          {errors.allowedOrigins ? (
            <span className="ne-field__error" role="alert">
              {errors.allowedOrigins}
            </span>
          ) : (
            <span style={muted}>
              Leave empty to allow any website (not recommended). For testing on your computer add
              e.g. http://localhost:5173.
            </span>
          )}
        </div>
        <TextField
          label="Chat title"
          value={form.settings.title}
          onChange={(e) => setSetting('title', e.target.value)}
          error={errors.title}
        />
        <TextField
          label="Greeting"
          value={form.settings.greeting}
          onChange={(e) => setSetting('greeting', e.target.value)}
          error={errors.greeting}
        />
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <TextField
            label="Colour"
            value={form.settings.color}
            onChange={(e) => setSetting('color', e.target.value)}
            error={errors.color}
          />
          <Select
            label="Position"
            value={form.settings.position}
            options={[
              { value: 'right', label: 'Bottom right' },
              { value: 'left', label: 'Bottom left' },
            ]}
            onChange={(e) => setSetting('position', e.target.value as 'right' | 'left')}
          />
        </div>
        <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
          <legend className="ne-field__label">Before the chat starts</legend>
          <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={form.settings.preChat.enabled}
              onChange={(e) => setPreChat('enabled', e.target.checked)}
            />
            Ask for the visitor&apos;s details first (pre-chat form)
          </label>
          {form.settings.preChat.enabled ? (
            <div style={{ display: 'flex', gap: 'var(--space-3)', paddingLeft: 'var(--space-4)' }}>
              {(
                [
                  ['askName', 'Name'],
                  ['askPhone', 'Phone'],
                  ['askEmail', 'Email'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={form.settings.preChat[key]}
                    onChange={(e) => setPreChat(key, e.target.checked)}
                  />
                  {label}
                </label>
              ))}
            </div>
          ) : (
            <span style={muted}>Off: your flows ask for details only when they need them.</span>
          )}
        </fieldset>
        {error ? (
          <p className="ne-auth__error" role="alert">
            {error}
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : channel ? 'Save' : 'Create website chat'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ConfirmModal({
  title,
  message,
  confirmLabel,
  busy,
  onConfirm,
  onClose,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal title={title} onClose={onClose}>
      <p style={{ marginTop: 0 }}>{message}</p>
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" disabled={busy} onClick={onConfirm}>
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

function WebsiteChatCard({ channel, canManage }: { channel: WebsiteChannel; canManage: boolean }) {
  const toast = useToast();
  const rotate = useRotateSiteKey(channel.channelId);
  const remove = useRemoveWebsiteChannel(channel.channelId);
  const [modal, setModal] = useState<'edit' | 'rotate' | 'remove' | null>(null);
  const snippet = installSnippet(channel.siteKey);

  async function copy() {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success('Snippet copied.');
    } catch {
      toast.error('Could not copy - select the snippet and copy it.');
    }
  }

  return (
    <Card title={channel.name}>
      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <span style={muted}>
          Paste this before <code>&lt;/body&gt;</code> on your website:
        </span>
        <pre
          aria-label="Install snippet"
          style={{
            margin: 0,
            padding: 'var(--space-2)',
            background: 'var(--color-surface-muted, rgba(127,127,127,0.12))',
            borderRadius: 'var(--radius-md)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {snippet}
        </pre>
        <span style={muted}>
          Allowed websites:{' '}
          {channel.allowedOrigins.length ? channel.allowedOrigins.join(', ') : 'any website'}
          {' · '}
          {channel.starterAutomationId ? (
            <Link to="/automations">Lead-capture automation on</Link>
          ) : (
            <Link to="/automations">Add an automation to reply to visitors</Link>
          )}
        </span>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Button type="button" variant="secondary" onClick={() => void copy()}>
            Copy snippet
          </Button>
          <a
            className="ne-btn ne-btn--secondary"
            href={`/webchat.html?key=${encodeURIComponent(channel.siteKey)}`}
            target="_blank"
            rel="noreferrer"
          >
            Preview
          </a>
          {canManage ? (
            <>
              <Button type="button" variant="secondary" onClick={() => setModal('edit')}>
                Edit
              </Button>
              <Button type="button" variant="secondary" onClick={() => setModal('rotate')}>
                New site key
              </Button>
              <Button type="button" variant="secondary" onClick={() => setModal('remove')}>
                Remove
              </Button>
            </>
          ) : null}
        </div>
      </div>
      {modal === 'edit' ? (
        <WebsiteChatModal channel={channel} onClose={() => setModal(null)} />
      ) : null}
      {modal === 'rotate' ? (
        <ConfirmModal
          title="Issue a new site key?"
          message="The current snippet stops working at once - paste the new one on your website."
          confirmLabel="Issue new key"
          busy={rotate.isPending}
          onClose={() => setModal(null)}
          onConfirm={() =>
            void rotate
              .mutateAsync()
              .then(() => {
                toast.success('New site key issued - update the snippet on your website.');
                setModal(null);
              })
              .catch((err: Error) => toast.error(err.message))
          }
        />
      ) : null}
      {modal === 'remove' ? (
        <ConfirmModal
          title="Remove this website chat?"
          message="The chat disappears from your website. Past chats stay in the Inbox."
          confirmLabel="Remove"
          busy={remove.isPending}
          onClose={() => setModal(null)}
          onConfirm={() =>
            void remove
              .mutateAsync()
              .then(() => {
                toast.success('Website chat removed.');
                setModal(null);
              })
              .catch((err: Error) => toast.error(err.message))
          }
        />
      ) : null}
    </Card>
  );
}

/**
 * Channels → Website chat (ADR-038): the workspace's chat widgets, their
 * install snippets and settings. Needs the "Website chat" menu (Enterprise
 * plan); otherwise a short note on how to get it.
 */
export function WebsiteChatSection() {
  const { hasMenuAccess, hasPermission } = useAuth();
  const enabled = hasMenuAccess(WEBSITE_CHAT_MENU);
  const canManage = hasPermission('channels.manage');
  const channels = useWebsiteChannels(enabled);
  const [adding, setAdding] = useState(false);

  if (!enabled) {
    return (
      <Card title="Website chat">
        <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
          Add a chat to your website that runs your automations - capture leads, book appointments
          and take enquiries. Included in the Enterprise plan.{' '}
          {hasPermission('settings.manage') ? (
            <Link to="/request-access">Request access</Link>
          ) : (
            'Ask your workspace owner to request access.'
          )}
        </p>
      </Card>
    );
  }

  return (
    <section style={{ display: 'grid', gap: 'var(--space-3)' }} aria-label="Website chat">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>Website chat</h2>
        {canManage ? (
          <Button type="button" onClick={() => setAdding(true)}>
            Add website chat
          </Button>
        ) : null}
      </div>
      {channels.isPending ? (
        <PageState kind="loading" message="Loading website chats…" />
      ) : channels.isError ? (
        <PageState kind="error" message={channels.error.message} />
      ) : channels.data.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
          No website chat yet. Add one, paste the snippet on your website, and visitors can chat
          with your automations - new leads appear under Leads.
        </p>
      ) : (
        channels.data.map((c) => (
          <WebsiteChatCard key={c.channelId} channel={c} canManage={canManage} />
        ))
      )}
      {adding ? <WebsiteChatModal channel={null} onClose={() => setAdding(false)} /> : null}
    </section>
  );
}
